import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Store, Briefcase, CalendarDays, FolderKanban, Building2, Luggage, Navigation, Globe, MapPin, Map as MapIcon, Loader2, Clock, RefreshCw, UserCheck, Info } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { regionLabel } from '../lib/regionNames';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const META = {
  shop: { lv: 'Veikals', en: 'Shop', ru: 'Магазин', Icon: Store },
  service: { lv: 'Pakalpojums', en: 'Service', ru: 'Услуга', Icon: Briefcase },
  event: { lv: 'Pasākums', en: 'Event', ru: 'Событие', Icon: CalendarDays },
  project: { lv: 'Projekts', en: 'Project', ru: 'Проект', Icon: FolderKanban },
  business: { lv: 'Uzņēmums', en: 'Business', ru: 'Компания', Icon: Building2 },
  classified: { lv: 'Sludinājums', en: 'Classified', ru: 'Объявление', Icon: Luggage },
};

const fmtDist = (m) => (m == null ? '' : m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);
const timeAgo = (iso, tr) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return tr(`pirms ${Math.max(1, Math.floor(diff / 60))} min`, `${Math.max(1, Math.floor(diff / 60))} min ago`, `${Math.max(1, Math.floor(diff / 60))} мин назад`);
  if (diff < 86400) return tr(`pirms ${Math.floor(diff / 3600)} h`, `${Math.floor(diff / 3600)} h ago`, `${Math.floor(diff / 3600)} ч назад`);
  const d = Math.floor(diff / 86400);
  if (d < 30) return tr(`pirms ${d} d.`, `${d} d ago`, `${d} д назад`);
  return new Date(iso).toLocaleDateString('lv-LV');
};

const PAGE = 12;
const POLL_MS = 30000;

// Persist the viewer's feed sort + location-mode choice across visits.
const SORT_KEY = 'kolv.feed.sort';
const LOCMODE_KEY = 'kolv.feed.locMode';
const readLS = (k) => { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* ignore */ } };

// Home-page activity feed ("logs"): everything active across the 6 catalogue
// categories. Sorting, geo-distance, category filtering and paging all happen
// SERVER-side (/api/map/feed) so the client stays fast as content grows; it
// also polls /api/map/feed/updates for an auto-refresh "N jauni" pill.
export default function ActivityFeed({ apiRef, mapWrapRef, selected, col }) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const tr = useCallback((lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? (ru ?? en) : en), [language]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Default to "Visa Latvija" so the feed is never empty and we never prompt on
  // load; a saved choice (localStorage) is restored, and a profile location
  // silently upgrades the default to "Tuvākie" (see effect below).
  const [sort, setSort] = useState(() => {
    const s = readLS(SORT_KEY);
    return (s === 'tuvakie' || s === 'visa_latvija') ? s : 'visa_latvija';
  });
  const [locMode, setLocMode] = useState(() => {
    const m = readLS(LOCMODE_KEY);
    return (m === 'located' || m === 'online') ? m : 'all';
  });
  const sortChosen = useRef(readLS(SORT_KEY) !== '');
  const [hasOnline, setHasOnline] = useState(false);
  const [badThumbs, setBadThumbs] = useState(() => new Set());
  const [userLoc, setUserLoc] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [radiusKm, setRadiusKm] = useState(null);    // null = Visi
  const askedBrowser = useRef(false);
  const seenNewest = useRef('');

  const serverSort = (sort === 'tuvakie' && userLoc) ? 'nearest' : 'recent';

  const params = useCallback((offset) => {
    const p = { sort: serverSort, offset, limit: PAGE };
    if (selected) p.category = selected;
    if (locMode !== 'all') p.loc_mode = locMode;
    if (serverSort === 'nearest') {
      p.lat = userLoc.lat; p.lng = userLoc.lng;
      if (radiusKm) p.radius_km = radiusKm;
    }
    return p;
  }, [serverSort, selected, userLoc, radiusKm, locMode]);

  const refreshBaseline = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/map/feed/updates`, { params: { since: '', ...(selected ? { category: selected } : {}) } });
      seenNewest.current = data.newest || '';
      setNewCount(0);
    } catch (e) { /* ignore */ }
  }, [selected]);

  const loadReset = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/map/feed`, { params: params(0) });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
      setHasOnline(!!data.has_online);
      refreshBaseline();
    } catch (e) { setItems([]); setTotal(0); setHasMore(false); }
    finally { setLoading(false); }
  }, [params, refreshBaseline]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await axios.get(`${API}/map/feed`, { params: params(items.length) });
      setItems((prev) => [...prev, ...(data.items || [])]);
      setHasMore(!!data.has_more);
    } catch (e) { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  // Reload whenever the sort, category, location, radius or loc mode changes.
  useEffect(() => { loadReset(); }, [serverSort, selected, userLoc, radiusKm, locMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Profile location first (no prompt).
  useEffect(() => {
    if (user && user.lat != null && user.lng != null) setUserLoc({ lat: user.lat, lng: user.lng });
  }, [user]);

  // Silently upgrade the default to "Tuvākie" only when a location is already
  // available (from the profile) and the viewer hasn't made an explicit choice.
  // Otherwise we stay on "Visa Latvija" — never prompting, never empty.
  useEffect(() => {
    if (!sortChosen.current && user && user.lat != null && user.lng != null) setSort('tuvakie');
  }, [user]);

  // Auto-refresh poll: count items newer than what we've shown.
  useEffect(() => {
    const id = setInterval(async () => {
      if (!seenNewest.current) return;
      try {
        const { data } = await axios.get(`${API}/map/feed/updates`, { params: { since: seenNewest.current, ...(selected ? { category: selected } : {}) } });
        setNewCount(data.new_count || 0);
      } catch (e) { /* ignore */ }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [selected]);

  const ensureLocation = useCallback(() => {
    if (userLoc || askedBrowser.current) return;
    if (user && user.lat != null && user.lng != null) { setUserLoc({ lat: user.lat, lng: user.lng }); return; }
    const fallback = () => { sortChosen.current = true; writeLS(SORT_KEY, 'visa_latvija'); setSort('visa_latvija'); };
    if (!navigator.geolocation) { fallback(); return; }
    askedBrowser.current = true;
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGeoBusy(false); },
      () => { setGeoBusy(false); fallback(); toast.info(tr('Atrašanās vieta nav pieejama — rādu visu Latviju.', 'Location unavailable — showing all Latvia.', 'Местоположение недоступно — показываю всю Латвию.')); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 },
    );
  }, [userLoc, user, tr]);

  // Persisted choice handlers (remembered across visits).
  const chooseSort = (v) => { sortChosen.current = true; writeLS(SORT_KEY, v); setSort(v); };
  const chooseLocMode = (v) => { writeLS(LOCMODE_KEY, v); setLocMode(v); };

  const pickTuvakie = () => { chooseSort('tuvakie'); ensureLocation(); };
  const place = user?.location_name || regionLabel(user?.region) || '';
  const effNearest = serverSort === 'nearest';

  const showOnMap = (e, it) => {
    e.stopPropagation();
    mapWrapRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    apiRef?.current?.fitExplore?.([it]);
  };

  return (
    <section className="mt-3 bg-white rounded-2xl border border-[#E5E4E0] p-4 sm:p-5" data-testid="activity-feed">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="w-4 h-4 text-[#2F7E79] shrink-0" />
          <h3 className="font-outfit text-base font-semibold text-[#121212] truncate">
            {selected ? tr(`Jaunākais: ${META[selected].lv}`, `Latest: ${META[selected].en}`, `Новое: ${META[selected].ru}`) : tr('Jaunākais', 'Latest activity', 'Последнее')}
          </h3>
          {!loading && <span className="text-sm text-[#9CA3AF] shrink-0">({total})</span>}
        </div>
        <div className="flex items-center rounded-full border border-[#E5E4E0] overflow-hidden shrink-0" data-testid="feed-sort">
          <button type="button" onClick={pickTuvakie} aria-pressed={sort === 'tuvakie'}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 transition-colors ${sort === 'tuvakie' ? 'bg-[#2F7E79] text-white' : 'bg-white text-[#5C5C5C] hover:bg-[#F3F2EE]'}`}
            data-testid="feed-sort-tuvakie">
            {geoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Navigation className="w-3.5 h-3.5" />}
            {tr('Tuvākie', 'Nearest', 'Ближайшие')}
          </button>
          <button type="button" onClick={() => chooseSort('visa_latvija')} aria-pressed={sort === 'visa_latvija'}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 transition-colors ${sort === 'visa_latvija' ? 'bg-[#121212] text-white' : 'bg-white text-[#5C5C5C] hover:bg-[#F3F2EE]'}`}
            data-testid="feed-sort-visalatvija">
            <Globe className="w-3.5 h-3.5" />
            {tr('Visa Latvija', 'All Latvia', 'Вся Латвия')}
          </button>
        </div>
      </div>

      {hasOnline && (
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="feed-loc-filter">
          <span className="text-xs text-[#9CA3AF]">{tr('Rādīt:', 'Show:', 'Показать:')}</span>
          {[
            { v: 'all', label: tr('Visi', 'All', 'Все') },
            { v: 'located', label: tr('Ar vietu', 'Located', 'С местом') },
            { v: 'online', label: tr('Tiešsaistē', 'Online', 'Онлайн') },
          ].map((o) => (
            <button key={o.v} type="button" onClick={() => chooseLocMode(o.v)} aria-pressed={locMode === o.v}
              className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${locMode === o.v ? 'bg-[#121212] text-white border-[#121212]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
              data-testid={`feed-loc-${o.v}`}>
              {o.label}
            </button>
          ))}
          <p className="w-full text-[11px] text-[#6B7280] flex items-start gap-1 mt-0.5" data-testid="feed-loc-hint">
            <Info className="w-3 h-3 shrink-0 mt-0.5" />
            <span>{tr('“Ar vietu” — ar adresi kartē · “Tiešsaistē” — pieejams attālināti visā Latvijā', '“Located” — has a map address · “Online” — available remotely across Latvia', '«С местом» — есть адрес на карте · «Онлайн» — доступно удалённо по всей Латвии')}</span>
          </p>
        </div>
      )}

      {newCount > 0 && (
        <button type="button" onClick={loadReset}
          className="w-full mb-3 flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#2F7E79]/10 text-[#2F7E79] hover:bg-[#2F7E79]/20 transition-colors"
          data-testid="feed-refresh">
          <RefreshCw className="w-4 h-4" />
          {tr(`${newCount} jauni ieraksti — atjaunot`, `${newCount} new — refresh`, `${newCount} новых — обновить`)}
        </button>
      )}

      {effNearest && place && (
        <p className="text-xs text-[#9CA3AF] mb-3 -mt-1 flex items-center gap-1" data-testid="feed-place">
          <MapPin className="w-3 h-3" /> {tr('Tuvu:', 'Near:', 'Рядом:')} {place}
        </p>
      )}
      {effNearest && (
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="feed-radius">
          <span className="text-xs text-[#9CA3AF]">{tr('Rādiuss:', 'Radius:', 'Радиус:')}</span>
          {[{ v: null, l: tr('Visi', 'All', 'Все') }, { v: 1, l: '1 km' }, { v: 5, l: '5 km' }, { v: 10, l: '10 km' }].map((o) => (
            <button key={o.l} type="button" onClick={() => setRadiusKm(o.v)} aria-pressed={radiusKm === o.v}
              className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${radiusKm === o.v ? 'bg-[#2F7E79] text-white border-[#2F7E79]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#2F7E79]'}`}
              data-testid={`feed-radius-${o.v == null ? 'all' : o.v}`}>
              {o.l}
            </button>
          ))}
        </div>
      )}

      {sort === 'tuvakie' && !userLoc && !geoBusy && (
        <p className="text-xs text-[#9CA3AF] mb-3 -mt-1" data-testid="feed-no-loc">
          {tr('Atļauj atrašanās vietu, lai kārtotu pēc tuvuma.', 'Allow location to sort by distance.', 'Разрешите геолокацию для сортировки по близости.')}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#2F7E79]" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#9CA3AF] text-center py-10" data-testid="feed-empty">
          {tr('Pagaidām nav aktivitātes.', 'No activity yet.', 'Пока нет активности.')}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="feed-list">
            {items.map((it, i) => {
              const meta = META[it.category] || META.shop;
              const Icon = meta.Icon;
              const mcol = col ? col(it.category) : '#2F7E79';
              const subtitle = it.subtitle || regionLabel(it.region) || '';
              return (
                <div key={`${it.category}-${it.id}-${i}`} onClick={() => it.link && navigate(it.link)}
                  className={`rounded-2xl border p-3 flex items-center gap-3 cursor-pointer hover:shadow-md transition-all ${it.is_friend ? 'bg-[#EEF4EF] border-[#4A6B53] ring-1 ring-[#4A6B53]/30' : 'bg-white border-[#F0EFEB]'}`}
                  data-testid={`feed-item-${i}`}>
                  {it.thumbnail && !badThumbs.has(it.thumbnail) ? (
                    <img src={it.thumbnail} alt="" loading="lazy"
                      onError={() => setBadThumbs((prev) => { if (prev.has(it.thumbnail)) return prev; const n = new Set(prev); n.add(it.thumbnail); return n; })}
                      className="w-14 h-14 rounded-xl object-cover shrink-0" data-testid={`feed-thumb-${i}`} />
                  ) : (
                    <span className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${mcol}1A` }} data-testid={`feed-thumb-fallback-${i}`}>
                      <Icon className="w-6 h-6" style={{ color: mcol }} />
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ backgroundColor: `${mcol}1A`, color: mcol }}>
                        {tr(meta.lv, meta.en, meta.ru)}
                      </span>
                      {it.is_friend && (
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#4A6B53] text-white" data-testid={`feed-friend-${i}`}>
                          <UserCheck className="w-3 h-3" /> {tr('Draugs', 'Friend', 'Друг')}
                        </span>
                      )}
                      {it.price != null && it.price !== '' && <span className="text-[11px] font-semibold text-[#D97736]">€{it.price}</span>}
                      {it.is_online && (
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#0F766E]/10 text-[#0F766E]">
                          <Globe className="w-3 h-3" /> {tr('Tiešsaistē', 'Online', 'Онлайн')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-[#121212] truncate">{it.name}</p>
                    <p className="flex items-center gap-1.5 text-[11px] text-[#9CA3AF] truncate">
                      {effNearest && it.distance_m != null
                        ? <span className="inline-flex items-center gap-1 font-semibold text-[#2F7E79]"><Navigation className="w-3 h-3" />{fmtDist(it.distance_m)}</span>
                        : <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(it.created_at, tr)}</span>}
                      {subtitle && <span className="inline-flex items-center gap-1 min-w-0 truncate"><MapPin className="w-3 h-3 shrink-0" />{subtitle}</span>}
                    </p>
                  </div>
                  {it.lat != null && (
                    <button type="button" onClick={(e) => showOnMap(e, it)}
                      className="flex items-center gap-1 text-[11px] font-semibold text-[#5C5C5C] hover:text-[#2563EB] px-2 py-1 rounded-full border border-[#E5E4E0] hover:border-[#2563EB] transition-colors shrink-0"
                      title={tr('Rādīt kartē', 'Show on map', 'Показать на карте')} data-testid={`feed-map-${i}`}>
                      <MapIcon className="w-3.5 h-3.5" /> {tr('Kartē', 'Map', 'Карта')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-4">
              <button type="button" onClick={loadMore} disabled={loadingMore}
                className="flex items-center gap-2 text-sm font-semibold px-5 py-2 rounded-full bg-[#F3F2EE] text-[#121212] hover:bg-[#ECEAE4] transition-colors disabled:opacity-60" data-testid="feed-more">
                {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {tr('Rādīt vairāk', 'Show more', 'Показать ещё')} ({total - items.length})
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
